import React, { useEffect, useRef } from "react";
import { CalendarCheck, Clock, Globe, ExternalLink } from "lucide-react";
import Reveal from "../components/Reveal";
import Carousel from "../components/Carousel";
import { LINKS, IMAGES } from "../mock";

// The 5 attached branded promo graphics (phone number updated to (727) 404-2693).
const SLIDES = [
  { image: "/carousel/post1.jpg", alt: "Nourish Your Body. Honor Your Intuition." },
  { image: "/carousel/post2.jpg", alt: "Reconnect With Your Body's Wisdom" },
  { image: "/carousel/post3.jpg", alt: "Restore Balance & Inner Harmony" },
  { image: "/carousel/post5.jpg", alt: "Feel Refreshed. Rejuvenated. Renewed." },
  { image: "/carousel/post6.jpg", alt: "Discover What the Stars May Reveal" },
];

const ZCAL_SRC = "https://static.zcal.co/embed/v1/embed.js";

export default function Booking() {
  const widgetRef = useRef(null);

  useEffect(() => {
    // The zcal embed script scans for `.zcal-inline-widget` once when it loads and
    // builds an embeddable iframe from the child <a href>. Because this is a SPA,
    // we (re)inject the script on every mount so it re-scans and renders the calendar.
    const script = document.createElement("script");
    script.src = ZCAL_SRC;
    script.async = true;
    script.type = "text/javascript";
    document.body.appendChild(script);
    return () => {
      if (script.parentNode) script.parentNode.removeChild(script);
    };
  }, []);

  return (
    <div>
      {/* Header */}
      <section className="bg-gradient-to-b from-[#effaf9] to-white pt-16 pb-8 md:pt-20 md:pb-10">
        <div className="max-w-4xl mx-auto px-5 text-center">
          <Reveal>
            <img
              src={IMAGES.kamlaAbout}
              alt="Kamla Das"
              className="w-48 md:w-56 h-auto rounded-xl shadow-md mx-auto mb-5"
            />
            <p className="text-xs tracking-[0.3em] uppercase text-[var(--teal-dark)]">
              Holistic Intuitive Nutrition
            </p>
            <h1 className="font-serif-brand text-5xl md:text-6xl text-purple mt-3">Book a Call</h1>
            <div className="brand-rule mx-auto mt-5" />
            <p className="text-neutral-600 mt-6 max-w-xl mx-auto leading-relaxed">
              Schedule your free 30-minute virtual consultation with Coach Kam.
            </p>
          </Reveal>

          <Reveal delay={120}>
            <div className="flex flex-wrap justify-center gap-6 mt-8 text-sm text-neutral-500">
              <span className="flex items-center gap-2">
                <Clock size={16} className="text-[var(--teal-dark)]" /> 30 minutes
              </span>
              <span className="flex items-center gap-2">
                <Globe size={16} className="text-[var(--teal-dark)]" /> Online / Worldwide
              </span>
              <span className="flex items-center gap-2">
                <CalendarCheck size={16} className="text-[var(--teal-dark)]" /> Free consultation
              </span>
            </div>
          </Reveal>
        </div>
      </section>

      {/* zcal inline embed */}
      <section className="max-w-4xl mx-auto px-5 py-8 md:py-10">
        <Reveal>
          <div className="rounded-2xl border border-neutral-100 shadow-sm bg-white overflow-hidden">
            <div
              ref={widgetRef}
              className="zcal-inline-widget"
              data-zcal-options='{"variant":"inline"}'
              style={{ minHeight: "640px" }}
            >
              <a href={LINKS.bookCall} target="_blank" rel="noopener noreferrer">
                Book a meeting with Coach Kam
              </a>
            </div>
          </div>
          <p className="text-center text-sm text-neutral-400 mt-4">
            Trouble viewing the calendar?{" "}
            <a
              href={LINKS.bookCall}
              target="_blank"
              rel="noopener noreferrer"
              className="text-[var(--teal-dark)] font-semibold inline-flex items-center gap-1 hover:underline"
            >
              Open the booking page <ExternalLink size={13} />
            </a>
          </p>
        </Reveal>
      </section>

      {/* Carousel slideshow (branded promo graphics) */}
      <section className="bg-[#f7fbfa] py-16 md:py-20 mt-6">
        <div className="max-w-6xl mx-auto px-5">
          <Reveal className="text-center mb-12">
            <p className="text-xs tracking-[0.3em] uppercase text-[var(--teal-dark)]">
              How we can support you
            </p>
            <h2 className="font-serif-brand text-4xl md:text-5xl text-purple mt-3">
              Your journey, your way
            </h2>
            <div className="brand-rule mx-auto mt-4" />
          </Reveal>
          <Reveal delay={120}>
            <Carousel slides={SLIDES} interval={5000} />
          </Reveal>
        </div>
      </section>
    </div>
  );
}
